import { useEffect, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';

const OAUTH_PENDING_KEY = 'oauth-in-progress';
const OAUTH_PENDING_TIMEOUT_MS = 120_000; // 2 minutes

// custom:groups values that are allowed to see the Non-Prod Security page.
// Decoded from the production JWT: custom:groups = "CSWEB_DASHBOARD_ADMIN".
// Update this list if PingID maps the target AD groups to a different value.
export const ALLOWED_SECURITY_GROUPS = ['CSWEB_DASHBOARD_ADMIN'];

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const getAuthToken = async () => {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (err) {
      console.error('extractTokenFromSession - error:', err);
      throw err;
    }
  };

  const authenticate = async () => {
    // Always try the session first. This handles the race condition where
    // Amplify completes the OAuth code exchange before the Hub listener attaches.
    try {
      const user = await Auth.currentSession();
      console.log(user);
      sessionStorage.removeItem(OAUTH_PENDING_KEY);
      setIsAuthenticated(true);
      return;
    } catch (err) {
      const isNoUser =
        err === 'No current user' ||
        (err instanceof Error && err.message === 'No current user');
      if (!isNoUser) {
        console.error('authenticate error:', err);
        return;
      }
    }

    // No valid session. Check whether an OAuth exchange is actively in progress.
    const pendingAt = sessionStorage.getItem(OAUTH_PENDING_KEY);
    if (pendingAt !== null) {
      const elapsed = Date.now() - Number(pendingAt);
      if (elapsed < OAUTH_PENDING_TIMEOUT_MS) {
        // Amplify is still exchanging the code; Hub will fire cognitoHostedUI.
        console.log('[auth] OAuth callback in progress, waiting for Hub event');
        return;
      }
      // Flag is stale (abandoned flow) — clear it and restart.
      console.log('[auth] OAuth pending flag expired, restarting sign in');
      sessionStorage.removeItem(OAUTH_PENDING_KEY);
    }

    console.log('[auth] No current user - starting federated sign in');
    sessionStorage.setItem(OAUTH_PENDING_KEY, String(Date.now()));
    await Auth.federatedSignIn({
      customProvider:
        process.env.REACT_APP_PINGID_PROVIDER || 'csweb-dashboard-pingid'
    });
  };

  useEffect(() => {
    const hubListenerCancelToken = Hub.listen(
      'auth',
      ({ payload: { event, data } }) => {
        console.log('[Hub auth event]', event, data);
        switch (event) {
          case 'signIn':
          case 'cognitoHostedUI':
            sessionStorage.removeItem(OAUTH_PENDING_KEY);
            setIsAuthenticated(true);
            break;
          case 'signOut':
            setIsAuthenticated(false);
            break;
          case 'signIn_failure':
          case 'cognitoHostedUI_failure':
            sessionStorage.removeItem(OAUTH_PENDING_KEY);
            setIsAuthenticated(false);
            console.log('[auth] Sign in failure', data);
            break;
          default:
            break;
        }
      }
    );
    return () => {
      hubListenerCancelToken();
    };
  }, [isAuthenticated]);

  const getUserGroups = async (): Promise<string[]> => {
    try {
      const session = await Auth.currentSession();
      const payload = session.getIdToken().decodePayload();
      const raw: string = payload['custom:groups'] ?? '';
      return raw
        .split(',')
        .map((g) => g.trim())
        .filter(Boolean);
    } catch {
      return [];
    }
  };

  return {
    isAuthenticated,
    authenticate,
    getAuthToken,
    getUserGroups
  };
};
